import {ActionList, ActionMenu, Button, Spinner} from '@primer/react'
import {CopilotIcon, TriangleDownIcon} from '@primer/octicons-react'
import {useCallback, useEffect, useState} from 'react'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import styles from './ModelPicker.module.css'

export interface ModelOption {
  id: string
  name: string
  icon?: string
  multiplier?: number
}

interface ModelsResponse {
  models: ModelOption[]
  current_model?: string
}

interface ModelPickerProps {
  modelsUrl: string
  onModelChange?: (modelId: string) => void
}

const FALLBACK_AUTO_MODEL: ModelOption = {id: 'auto', name: 'Auto', multiplier: 1}

export function ModelPicker({modelsUrl, onModelChange}: ModelPickerProps) {
  const [models, setModels] = useState<ModelOption[]>([FALLBACK_AUTO_MODEL])
  const [selectedModelId, setSelectedModelId] = useState(FALLBACK_AUTO_MODEL.id)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    async function fetchModels() {
      setIsLoading(true)
      try {
        const response = await verifiedFetchJSON(modelsUrl)
        if (response.ok) {
          const data: ModelsResponse = await response.json()
          if (data.models.length > 0) {
            setModels(data.models)
          }
          const defaultModelId = data.current_model || data.models[0]?.id || null
          if (defaultModelId) {
            setSelectedModelId(defaultModelId)
            if (data.current_model) {
              onModelChange?.(data.current_model)
            }
          }
        }
      } catch {
        // Silently fail — picker will show "Auto" only
      } finally {
        setIsLoading(false)
      }
    }

    void fetchModels()
  }, [modelsUrl, onModelChange])

  const handleSelect = useCallback(
    (modelId: string) => {
      setSelectedModelId(modelId)
      onModelChange?.(modelId)
    },
    [onModelChange],
  )

  const selectedModel = models.find(m => m.id === selectedModelId)
  const displayName = selectedModel?.name ?? 'Auto'

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Button size="small" variant="invisible" className={styles.pickerButton} trailingVisual={TriangleDownIcon}>
          <span className="sr-only">Model: </span>
          <span data-testid="model-picker-selected">{displayName}</span>
        </Button>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium">
        {isLoading ? (
          <div className={styles.spinnerContainer}>
            <Spinner size="small" />
          </div>
        ) : (
          <ActionList selectionVariant="single">
            <ActionList.Group>
              <ActionList.GroupHeading>Models</ActionList.GroupHeading>
              {models.map(model => (
                <ActionList.Item
                  key={model.id}
                  selected={model.id === selectedModelId}
                  onSelect={() => handleSelect(model.id)}
                >
                  {model.icon && model.id !== 'auto' ? (
                    <ActionList.LeadingVisual>
                      <img
                        data-testid={`model-picker-button-icon-${model.id}`}
                        src={model.icon}
                        alt=""
                        width={16}
                        height={16}
                        className={styles.modelIcon}
                      />
                    </ActionList.LeadingVisual>
                  ) : (
                    <ActionList.LeadingVisual>
                      <CopilotIcon size={16} />
                    </ActionList.LeadingVisual>
                  )}
                  {model.name}
                  {model.multiplier !== undefined && (
                    <ActionList.TrailingVisual>
                      <span className={styles.multiplierBadge}>{Number(model.multiplier.toFixed(2))}x</span>
                    </ActionList.TrailingVisual>
                  )}
                </ActionList.Item>
              ))}
            </ActionList.Group>
          </ActionList>
        )}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
